// src\hooks\useAnalyticsTracker.js
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useUserStore } from '../store/userStore';

export const useAnalyticsTracker = () => {
  const location = useLocation();
  const locationRef = useRef(location);

  const currUserActive = useUserStore((state) => state.currUserActive);
  const userRef = useRef(currUserActive);

  const visitorIdRef = useRef(null);
  const currentVisitIdRef = useRef(crypto.randomUUID());
  const heartbeatTimerRef = useRef(null);
  const accumulatedSecondsRef = useRef(0);

  const getTimeOfDayBucket = () => {
    const hours = new Date().getHours();
    if (hours >= 0 && hours < 4) return 'Midnight';
    if (hours >= 4 && hours < 8) return 'Early Morning';
    if (hours >= 8 && hours < 12) return 'Morning';
    if (hours >= 12 && hours < 17) return 'Afternoon';
    if (hours >= 17 && hours < 21) return 'Evening';
    return 'Night';
  };

  // FIXED: device detection.
  // - Client Hints (navigator.userAgentData) are the most reliable signal
  //   when the browser supports them (all Chromium browsers) and are
  //   checked first.
  // - UA fallback now checks tablet signals BEFORE the mobile regex,
  //   because Android tablets keep "Android" in their UA but drop
  //   "Mobile" — the old code matched "Android" alone as mobile, so
  //   every Android tablet got miscounted.
  // - iPadOS 13+ reports as "Macintosh" in the UA; multi-touch is the
  //   only reliable signal, so that's checked explicitly.
  const getDeviceDetails = () => {
    const ua = navigator.userAgent;
    const uaData = navigator.userAgentData;
    const touchPoints = navigator.maxTouchPoints || 0;
    const screenWidth = window.screen?.width || 0;

    let deviceType = 'Desktop';

    if (uaData) {
      if (uaData.mobile) {
        deviceType = screenWidth >= 768 ? 'Tablet' : 'Mobile';
      } else {
        deviceType = 'Desktop';
      }
    } else {
      const isIPad = /iPad/i.test(ua) || (/Macintosh/i.test(ua) && touchPoints > 1);
      const isAndroidTablet = /Android/i.test(ua) && !/Mobile/i.test(ua);
      const isTabletUA = /Tablet/i.test(ua) || isIPad || isAndroidTablet;
      const isMobileUA = !isTabletUA && (/Mobi|iPhone/i.test(ua) || (/Android/i.test(ua) && /Mobile/i.test(ua)));

      if (isTabletUA) deviceType = 'Tablet';
      else if (isMobileUA) deviceType = 'Mobile';
      else deviceType = 'Desktop';
    }

    // FIXED: Edge's UA also contains "Chrome", so Edge must be checked
    // before the Chrome match or it always resolves to "Chrome".
    let browser = 'Unknown';
    if (ua.indexOf('Edg') > -1) browser = 'Edge';
    else if (ua.indexOf('Chrome') > -1) browser = 'Chrome';
    else if (ua.indexOf('Firefox') > -1) browser = 'Firefox';
    else if (ua.indexOf('Safari') > -1) browser = 'Safari';

    return {
      device_type: deviceType,
      browser,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      initial_period: getTimeOfDayBucket()
    };
  };

  const invokeEdgeTracking = async (payload) => {
    try {
      const { error } = await supabase.functions.invoke('track-session', {
        body: {
          visitor_id: visitorIdRef.current,
          user_id: userRef.current?.id || null,
          device_fingerprint: getDeviceDetails(),
          ...payload
        }
      });
      if (error) console.error('[Tracker Error]:', error.message);
    } catch (err) {
      console.error('[Tracker Invocation Failed]:', err);
    }
  };

  const flushTrackingData = () => {
    if (accumulatedSecondsRef.current === 0) return;

    const secondsToSend = accumulatedSecondsRef.current;
    accumulatedSecondsRef.current = 0;

    invokeEdgeTracking({
      flush_payload: {
        visit_id: currentVisitIdRef.current,
        path: locationRef.current.pathname,
        additional_seconds: secondsToSend
      }
    });
  };

  useEffect(() => {
    let storedId = localStorage.getItem('analytics_visitor_id');
    if (!storedId) {
      storedId = 'v_' + crypto.randomUUID();
      localStorage.setItem('analytics_visitor_id', storedId);
    }
    visitorIdRef.current = storedId;

    invokeEdgeTracking({
      visit_payload: {
        visit_id: currentVisitIdRef.current,
        path: locationRef.current.pathname
      }
    });

    heartbeatTimerRef.current = setInterval(() => {
      accumulatedSecondsRef.current += 1;

      if (accumulatedSecondsRef.current % 60 === 0) {
        flushTrackingData();
      }
    }, 1000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        flushTrackingData();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(heartbeatTimerRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      flushTrackingData();
    };
  }, []);

  useEffect(() => {
    if (location.pathname !== locationRef.current.pathname) {
      flushTrackingData();
      locationRef.current = location;

      invokeEdgeTracking({
        visit_payload: {
          visit_id: currentVisitIdRef.current,
          path: location.pathname
        }
      });
    }
  }, [location]);

  useEffect(() => {
    if (currUserActive?.id && !userRef.current?.id) {
      userRef.current = currUserActive;
      invokeEdgeTracking({
        user_id: currUserActive.id,
        flush_payload: {
          visit_id: currentVisitIdRef.current,
          path: location.pathname,
          additional_seconds: accumulatedSecondsRef.current
        }
      });
      accumulatedSecondsRef.current = 0;
    } else {
      userRef.current = currUserActive;
    }
  }, [currUserActive]);
};